// Maps the online claim form's flat model (see @/data/claim-form-fields) to the
// backend ClaimSubmissionDto shape ({ header, body }) consumed by
// POST /motor-assessment/api/claims/submit.

// Trim strings and normalise empty values to null so optional fields are
// omitted rather than sent blank; the backend treats null and blank alike.
export const str = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : value
  return trimmed === '' || trimmed === undefined ? null : trimmed
}

// yes/no fields store the literal 'Yes' / 'No' button values.
export const bool = (value) => {
  if (value === 'Yes') return true
  if (value === 'No') return false
  return null
}

// A repeating section carries a single entry from the form; include it only
// when at least one of its fields has a value.
export const list = (entry) =>
  Object.values(entry).some((value) => value !== null) ? [entry] : []

export function toClaimSubmission(form, attachments) {
  const f = form || {}

  const header = {
    regNo: str(f.vehicleRegistrationNumber),
    phoneNumber: str(f.insuredMobileNumber),
  }

  const body = {
    insured: {
      name: str(f.insuredName),
      address: str(f.insuredPostalAddress),
      tel: str(f.insuredMobileNumber),
      businessOccupation: str(f.insuredOccupation),
    },
    policy: {
      number: str(f.policyNumber),
      expiryDate: str(f.policyExpiryDate),
      hirePurchaseCompany: str(f.policyFinanceCompany),
    },
    vehicle: {
      makeModel: str([f.vehicleMake, f.vehicleModel].filter(Boolean).join(' ')),
      hpCc: str(f.vehicleEngineCapacity),
      regNo: str(f.vehicleRegistrationNumber),
      carryingCapacity: str(f.vehicleCarryingCapacity),
      trailerRegNo: str(f.trailerRegistrationNumber),
      trailerCarryingCapacity: str(f.trailerCarryingCapacity),
      ownerName: str(f.vehicleOwnerName),
      usePurpose: str(f.vehicleUsePurpose),
    },
    commercial: {
      goodsDescription: str(f.commercialGoodsDescription),
      goodsOwnerName: str(f.commercialGoodsOwner),
      trailerAttached: bool(f.commercialTrailerAttached),
      loadWeight: str(f.commercialVehicleLoadWeight),
    },
    driver: {
      name: str(f.driverName),
      occupation: str(f.driverOccupation),
      dateOfBirth: str(f.driverDateOfBirth),
      address: str(f.driverAddress),
      tel: str(f.driverMobileNumber),
      employedByInsured: bool(f.driverEmployedByInsured),
      timeInService: str(f.driverServiceLength),
      hadPermission: bool(f.driverHadPermission),
      timeDrivingVehicles: str(f.driverDrivingYears),
      toBlame: bool(f.driverToBlame),
      admittedLiability: bool(f.driverAdmittedLiability),
      previousAccidents: bool(f.driverPreviousAccidents),
      previousAccidentsDetails: str(f.driverPreviousAccidentDetails),
      pendingCharges: bool(f.driverConvictions),
      pendingChargesDetails: str(f.driverConvictionDetails),
      licenceType: str(f.driverLicenceType),
      testPassedDate: str(f.driverTestPassYear),
      licenceNo: str(f.driverLicenceNumber),
      ownsVehicle: bool(f.driverOwnsVehicle),
      vehicleInsurer: str(f.driverOwnInsurer),
      policyNo: str(f.driverOwnPolicyNumber),
    },
    accident: {
      date: str(f.accidentDate),
      time: str(f.accidentTime),
      place: str(f.accidentPlace),
      roadSurfaceType: str(f.accidentRoadSurface),
      visibility: str(f.accidentVisibility),
      surfaceWetOrDry: str(f.accidentWetOrDry),
      lightsShowing: str(f.accidentVehicleLights),
      warningGiven: str(f.accidentDriverWarning),
      estimatedSpeed: str(f.accidentEstimatedSpeed),
      weatherConditions: str(f.accidentWeatherConditions),
      policeTookParticulars: bool(f.policeTookParticulars),
      constableNo: str(f.policeConstableDetails),
      reportedPoliceStation: str(f.policeStationReported),
    },
    damage: {
      apparentDamage: str(f.apparentDamage),
      repairerNameAddress: str(f.repairerName),
      repairerTel: str(f.repairerMobileNumber),
      vehicleStillInUse: bool(f.vehicleStillInUse),
      inspectionWhenWhere: str(f.vehicleInspectionLocation),
    },
    otherVehicles: list({
      ownerNameAddress: str(f.otherVehicleOwner),
      regNo: str(f.otherVehicleRegistrationNumber),
      insurer: str(f.otherVehicleInsurer),
      otherPropertyDamaged: str(f.otherPropertyDamaged),
      driverNameAddress: str(f.otherVehicleDriver),
    }),
    injuredPersons: list({
      nameAddress: str(f.injuredPersonName),
      relationshipToInsured: str(f.injuredPersonRelationship),
      regNo: str(f.injuredPersonVehicleRegistration),
      apparentInjuries: str(f.injuredPersonInjuries),
    }),
    witnesses: list({
      name: str(f.witnessName),
      address: str(f.witnessAddress),
    }),
    passengers: list({
      name: str(f.passengerName),
      address: str(f.passengerAddress),
    }),
    driverStatement: str(f.driverStatement),
    ownerInsuredStatement: str(f.ownerStatement),
    declarationDate: str(f.declarationDate),
    insuredSigned: !!f.declaration,
    attachments: Array.isArray(attachments) ? attachments : [],
  }

  return { header, body }
}
